/**
 * Abstract Style Management System - Coordinator Engine
 * Coordinates SDK operations and delegates decomposition to utility functions.
 * @module abstractStyles/engine
 */

import type { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import { getAllowedPropertiesForAction } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import { ComparisonMode } from '../../types';
import { CSSPropertiesEngine } from './CSSPropertiesEngine';
import { CSSCustomPropertiesEngine } from './CSSCustomPropertiesEngine';
import { StyleEngineBase } from './StyleEngineBase';
import { StyleMetadataEngine } from './StyleMetadataEngine';
import type {
  StyleSDKClient,
  PropertyMetadata,
  PropertyDiscoveryResult,
  AggregatedStyleValues,
} from './types';
import { IStyleValuesProvider } from './IStyleValuesProvider';
import { decomposeCSSProperties } from './decompositionUtils';
import { decomposePropertiesWithDefinitionsAndNesting } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import {
  isShorthand,
  getShorthandAtomics,
} from '../grouping/propertyRelationships';
import { CssPropertyEnumCssPropertyType } from '@wix/ambassador-devcenter-components-v1-component/types';
import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import { ThemesProvider } from './ThemesProvider';
import { GD_WDS_EXPERIMENT } from '../../experiments';
import { GLOBAL_DESIGN_GROUPS_ACTIONS } from '../../actions';
/**
 * Global Design Style Engine - Full-Featured SDK-Based Engine
 *
 * Provides SDK operations:
 * - Property discovery from manifests
 * - Asynchronous value fetching
 * - Smart property updates with sibling propagation
 * - Shorthand recomposition
 *
 * Uses decomposition utilities (shared with StyleMetadataEngine).
 */
/**
 * Internal update queue item
 */
interface QueuedUpdate {
  changes: Partial<Record<string, string>>;
  isDirect: boolean;
  resolve: (values: AggregatedStyleValues) => void;
  reject: (error: Error) => void;
}

export class GlobalDesignStyleEngine
  extends StyleEngineBase
  implements IStyleValuesProvider
{
  private cssPropertiesEngine: CSSPropertiesEngine;
  private customPropertiesEngine: CSSCustomPropertiesEngine;
  private actionName: StylesDerivedNativeActionNames;
  private sdkClient: StyleSDKClient;
  private componentsAPI: ComponentsPlatformService;
  private withVariablesResolution: boolean;
  // Stateful properties
  private currentMetadata?: Record<string, PropertyMetadata>;
  private currentValues?: AggregatedStyleValues;

  // Update queue
  private updateQueue: QueuedUpdate[] = [];
  private processingPromise: Promise<void> | null = null;
  private isCancelled: boolean = false;

  // Configuration
  private shouldRefetchAfterUpdate: boolean = false;

  constructor(
    actionName: StylesDerivedNativeActionNames,
    sdkClient: StyleSDKClient,
    componentsAPI: ComponentsPlatformService,
    config?: {
      withVariablesResolution?: boolean;
      shouldRefetchAfterUpdate?: boolean;
      allowedProperties?: string[];
    },
  ) {
    super(config?.allowedProperties);
    this.actionName = actionName;
    this.componentsAPI = componentsAPI;
    this.sdkClient = sdkClient;
    this.withVariablesResolution = config?.withVariablesResolution ?? false;
    this.shouldRefetchAfterUpdate = config?.shouldRefetchAfterUpdate ?? false;

    const allowedCssPropertyTypes = getAllowedPropertiesForAction(actionName);

    // Determine sibling behavior from action config
    const actionConfig = GLOBAL_DESIGN_GROUPS_ACTIONS[actionName];
    const updateSiblings =
      actionConfig?.comparisonMode === ComparisonMode.ValueSet;

    // Create specialized engines for updates
    this.cssPropertiesEngine = new CSSPropertiesEngine(
      allowedCssPropertyTypes,
      updateSiblings,
      sdkClient,
    );

    // Get theme variables for CSS variable resolution in custom properties engine
    const themesProvider = new ThemesProvider(this.componentsAPI);
    const themeVariables = themesProvider.themeVariables;

    this.customPropertiesEngine = new CSSCustomPropertiesEngine(
      allowedCssPropertyTypes,
      sdkClient,
      themeVariables,
    );
  }

  /**
   * Creates GlobalDesignStyleEngine from existing StyleMetadataEngine.
   * Avoids redundant SDK calls by reusing pre-fetched data.
   *
   * @param metadataEngine - Source StyleMetadataEngine (from group)
   * @param actionName - Action name for the SDK engine
   * @param sdkClient - SDK client for future operations
   * @param config - Optional configuration
   * @returns New GlobalDesignStyleEngine with pre-populated state
   *
   * @example
   * const sdkEngine = GlobalDesignStyleEngine.fromMetadataEngine(
   *   group.engine,
   *   'borderStyles',
   *   sdkClient,
   * );
   */
  static fromMetadataEngine(
    metadataEngine: StyleMetadataEngine,
    actionName: StylesDerivedNativeActionNames,
    sdkClient: StyleSDKClient,
    componentsAPI: ComponentsPlatformService,
    config?: {
      shouldRefetchAfterUpdate?: boolean;
    },
  ): GlobalDesignStyleEngine {
    // Transfer allowed properties from metadata engine (group scope)
    const allowedProperties = metadataEngine.getAllowedProperties();
    const sdkEngine = new GlobalDesignStyleEngine(
      actionName,
      sdkClient,
      componentsAPI,
      {
        ...config,
        allowedProperties,
      },
    );

    // Transfer metadata only — values will be lazily fetched on first use
    // so that updates compare against fresh SDK state, not a stale snapshot.
    sdkEngine.currentMetadata = metadataEngine.getMetadata();

    return sdkEngine;
  }

  /**
   * Discovers properties from both engines and merges metadata.
   * Filters to allowed properties if scoping is enabled.
   */
  async discoverProperties(): Promise<PropertyDiscoveryResult> {
    // Fetch from SDK
    const [cssPropsMetadata, customPropsMetadata] = await Promise.all([
      this.cssPropertiesEngine.discoverProperties(),
      this.customPropertiesEngine.discoverProperties(),
    ]);

    // Merge metadata from both engines
    let metadata: Record<string, PropertyMetadata> = {
      ...cssPropsMetadata,
      ...customPropsMetadata,
    };

    // Filter to allowed properties (group scope)
    metadata = this.filterRecord(metadata);

    return {
      metadata,
      available: new Set(Object.keys(metadata)),
    };
  }

  /**
   * Initializes engine by discovering properties and fetching initial values.
   * Must be called before any operations.
   *
   * @returns Initial aggregated values
   */
  async initialize(): Promise<AggregatedStyleValues> {
    // Discover properties
    const { metadata } = await this.discoverProperties();
    this.currentMetadata = metadata;

    // Get initial values
    const values = await this.getAllValuesInternal(metadata);
    this.currentValues = values;

    return values;
  }

  /**
   * Gets all values from SDK (IStyleValuesProvider interface method).
   * Delegates to internal method.
   *
   * @param metadata - Property metadata from discoverProperties()
   * @returns Promise<AggregatedStyleValues> with mode discrimination
   */
  async getAllValues(
    metadata: Record<string, PropertyMetadata>,
  ): Promise<AggregatedStyleValues> {
    return this.getAllValuesInternal(metadata);
  }

  /**
   * Gets current values without refetching from SDK.
   * Returns undefined if engine not initialized.
   */
  getCurrentValues(): AggregatedStyleValues | undefined {
    return this.currentValues;
  }

  /**
   * Gets current metadata without refetching from SDK.
   * Returns undefined if engine not initialized.
   */
  getCurrentMetadata(): Record<string, PropertyMetadata> | undefined {
    return this.currentMetadata;
  }

  /**
   * Cancels all pending operations and clears queue.
   * Rejects all pending promises.
   */
  cancel(): void {
    this.isCancelled = true;

    // Reject all pending updates
    this.updateQueue.forEach((update) => {
      update.reject(new Error('Engine cancelled'));
    });
    this.updateQueue = [];
    this.processingPromise = null;
  }

  /**
   * Refetches all values from SDK and notifies listeners.
   *
   * @returns Fresh aggregated values
   * @throws Error if engine not initialized
   */
  async refetch(): Promise<AggregatedStyleValues> {
    if (!this.currentMetadata) {
      throw new Error('Engine not initialized - call initialize() first');
    }

    const values = await this.getAllValuesInternal(this.currentMetadata);
    this.currentValues = values;
    return values;
  }

  /**
   * Internal method to get all values from SDK.
   * Used by initialize() and refetch().
   *
   * @private
   * @param metadata - Property metadata from discoverProperties()
   * @returns Promise<AggregatedStyleValues> with mode discrimination
   */
  private async getAllValuesInternal(
    metadata: Record<string, PropertyMetadata>,
  ): Promise<AggregatedStyleValues> {
    // Fetch from SDK
    // Split metadata by namespace
    const cssPropsMetadata: Record<string, PropertyMetadata> = {};
    const customPropsMetadata: Record<string, PropertyMetadata> = {};

    for (const [key, meta] of Object.entries(metadata)) {
      if (meta.namespace === 'cssProperties') {
        cssPropsMetadata[key] = meta;
      } else {
        customPropsMetadata[key] = meta;
      }
    }

    const hasCSSProps = Object.keys(cssPropsMetadata).length > 0;
    const hasCustomProps = Object.keys(customPropsMetadata).length > 0;

    // Get raw values from SDK via specialized engines
    // Note: Sub-engines already decompose, so we get both raw and decomposed
    const [cssPropsValuesRaw, customPropsValuesRaw] = await Promise.all([
      hasCSSProps
        ? this.cssPropertiesEngine.getAllValues(cssPropsMetadata)
        : Promise.resolve({ raw: {}, decomposed: {} }),
      hasCustomProps
        ? this.customPropertiesEngine.getAllValues(customPropsMetadata)
        : Promise.resolve({ raw: {}, decomposed: {} }),
    ]);

    // Filter values to allowed properties (group scope)
    const cssPropsValues = {
      raw: this.filterRecord(cssPropsValuesRaw.raw) as Record<string, string>,
      decomposed: this.filterRecord(cssPropsValuesRaw.decomposed) as Record<
        string,
        any[]
      >,
    };
    const customPropsValues = {
      raw: this.filterRecord(customPropsValuesRaw.raw) as Record<
        string,
        string
      >,
      decomposed: this.filterRecord(customPropsValuesRaw.decomposed) as Record<
        string,
        any[]
      >,
    };

    // Get definitions from SDK and filter to allowed properties
    const definitionsRaw = await this.sdkClient.getStyleDefinitions();
    const definitions = {
      cssProperties: this.filterRecord(definitionsRaw.cssProperties) as Record<
        string,
        any
      >,
      cssCustomProperties: this.filterRecord(
        definitionsRaw.cssCustomProperties,
      ) as Record<string, any>,
    };

    const isGdWdsEnabled =
      this.componentsAPI.experimentsAPI?.enabled(GD_WDS_EXPERIMENT) ?? false;

    if (isGdWdsEnabled || this.withVariablesResolution) {
      return super.deriveAggregatedValues(
        cssPropsValues.raw,
        customPropsValues.raw,
        definitions.cssProperties,
        definitions.cssCustomProperties,
        metadata,
        this.componentsAPI,
      );
    }

    // Without variable resolution: use sub-engine decomposed values directly
    if (hasCSSProps && hasCustomProps) {
      return {
        mode: 'mixed',
        raw: {
          cssProperties: cssPropsValues.raw,
          cssCustomProperties: customPropsValues.raw,
        },
        decomposed: {
          cssProperties: cssPropsValues.decomposed,
          cssCustomProperties: customPropsValues.decomposed,
        },
        metadata,
        definitions: {
          cssProperties: definitions.cssProperties,
          cssCustomProperties: definitions.cssCustomProperties,
        },
      };
    } else if (hasCSSProps) {
      return {
        mode: 'cssProperties',
        raw: {
          cssProperties: cssPropsValues.raw,
        },
        decomposed: {
          cssProperties: cssPropsValues.decomposed,
        },
        metadata,
        definitions: {
          cssProperties: definitions.cssProperties,
        },
      };
    } else {
      return {
        mode: 'cssCustomProperties',
        raw: {
          cssCustomProperties: customPropsValues.raw,
        },
        decomposed: {
          cssCustomProperties: customPropsValues.decomposed,
        },
        metadata,
        definitions: {
          cssCustomProperties: definitions.cssCustomProperties,
        },
      };
    }
  }

  /**
   * Internal method to update properties with smart propagation.
   *
   * @private
   * @param changes - Properties to change (key-value pairs)
   * @param currentValues - Current aggregated values
   * @returns Promise<AggregatedStyleValues> - The new state after successful update
   */
  private async updatePropertiesInternal(
    changes: Partial<Record<string, string>>,
    currentValues: AggregatedStyleValues,
  ): Promise<AggregatedStyleValues> {
    const { cssPropsChanges, customPropsChanges } =
      this.partitionChangesByNamespace(changes, currentValues.metadata);

    // Get updates from both engines in parallel
    // Each engine receives cssPropertyType-keyed changes and filters based on its metadata
    const cssPropsRaw =
      currentValues.mode === 'cssCustomProperties'
        ? {}
        : currentValues.raw.cssProperties;
    const cssPropsDecomposed =
      currentValues.mode === 'cssCustomProperties'
        ? {}
        : currentValues.decomposed.cssProperties;

    const cssPropsPromise: Promise<Record<string, string>> =
      Object.keys(cssPropsChanges).length > 0
        ? this.cssPropertiesEngine.updateProperties(cssPropsChanges, {
            raw: cssPropsRaw,
            decomposed: cssPropsDecomposed,
            metadata: this.filterMetadataByNamespace(
              currentValues.metadata,
              'cssProperties',
            ),
          })
        : Promise.resolve({});

    const customPropsPromise: Promise<Record<string, string>> =
      Object.keys(customPropsChanges).length > 0
        ? this.customPropertiesEngine.updateProperties(
            customPropsChanges,
            this.filterMetadataByNamespace(
              currentValues.metadata,
              'cssCustomProperties',
            ),
          )
        : Promise.resolve({});

    const [cssPropsUpdates, customPropsUpdates] = await Promise.all([
      cssPropsPromise,
      customPropsPromise,
    ]);

    // Apply consolidated updates in single SDK call
    const hasUpdates =
      Object.keys(cssPropsUpdates).length > 0 ||
      Object.keys(customPropsUpdates).length > 0;

    if (hasUpdates) {
      await this.sdkClient.setStyles({
        ...(Object.keys(cssPropsUpdates).length > 0 && {
          cssProperties: cssPropsUpdates,
        }),
        ...(Object.keys(customPropsUpdates).length > 0 && {
          cssCustomProperties: customPropsUpdates,
        }),
      });
    }

    // Merge updates with current state to create new aggregated values
    return this.mergeUpdatesWithCurrentValues(
      currentValues,
      cssPropsUpdates,
      customPropsUpdates,
    );
  }

  /**
   * Build reverse index: cssPropertyType -> which namespaces expose that type.
   * Used to route changes that are provided by cssPropertyType rather than by metadata key.
   */
  private buildTypeToNamespacesIndex(
    metadata: Record<string, PropertyMetadata>,
  ): Map<string, Set<'cssProperties' | 'cssCustomProperties'>> {
    const index = new Map<
      string,
      Set<'cssProperties' | 'cssCustomProperties'>
    >();

    for (const meta of Object.values(metadata)) {
      const existing =
        index.get(meta.cssPropertyType) ??
        new Set<'cssProperties' | 'cssCustomProperties'>();
      existing.add(meta.namespace);
      index.set(meta.cssPropertyType, existing);
    }

    return index;
  }

  /**
   * Resolve a change key to a namespace via cssPropertyType lookup when it doesn't exist as a direct metadata key.
   *
   * Returns:
   * - 'cssProperties' | 'cssCustomProperties' for unambiguous matches
   * - null for unknown/ambiguous (exists in both namespaces or not present)
   */
  private resolveNamespaceByCssPropertyType(
    key: string,
    typeToNamespaces: Map<string, Set<'cssProperties' | 'cssCustomProperties'>>,
  ): 'cssProperties' | 'cssCustomProperties' | null {
    const namespaces = typeToNamespaces.get(key);
    if (!namespaces || namespaces.size !== 1) {
      return null;
    }
    return Array.from(namespaces)[0] ?? null;
  }

  /**
   * Split changes into cssProperties and cssCustomProperties buckets.
   *
   * Important behaviors:
   * - If key exists in metadata, route by its namespace
   * - If key is a cssCustomProperties metadata key (custom property ID), convert to cssPropertyType for the custom engine
   * - If key isn't in metadata, attempt cssPropertyType lookup; if ambiguous/unknown, forward to both engines
   */
  private partitionChangesByNamespace(
    changes: Partial<Record<string, string>>,
    metadata: Record<string, PropertyMetadata>,
  ): {
    cssPropsChanges: Record<string, string>;
    customPropsChanges: Record<string, string>;
  } {
    const cssPropsChanges: Record<string, string> = {};
    const customPropsChanges: Record<string, string> = {};
    const typeToNamespaces = this.buildTypeToNamespacesIndex(metadata);
    const hasCssPropsNamespace = Object.values(metadata).some(
      (m) => m.namespace === 'cssProperties',
    );
    const hasCustomPropsNamespace = Object.values(metadata).some(
      (m) => m.namespace === 'cssCustomProperties',
    );

    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) {
        continue;
      }

      const meta = metadata[key];
      if (meta) {
        if (meta.namespace === 'cssProperties') {
          cssPropsChanges[key] = value;
        } else {
          // Custom property ID -> cssPropertyType for custom properties engine
          customPropsChanges[meta.cssPropertyType] = value;
        }
        continue;
      }

      const resolved = this.resolveNamespaceByCssPropertyType(
        key,
        typeToNamespaces,
      );
      if (resolved === 'cssProperties') {
        cssPropsChanges[key] = value;
      } else if (resolved === 'cssCustomProperties') {
        customPropsChanges[key] = value;
      } else {
        // Ambiguous/unknown:
        // - In mixed mode we forward to both engines; each engine filters against its metadata.
        // - In single-namespace mode we forward only to the existing namespace to avoid
        //   accidental updates in the other namespace (notably cssProperties var() bypass).
        if (hasCssPropsNamespace && hasCustomPropsNamespace) {
          cssPropsChanges[key] = value;
          customPropsChanges[key] = value;
        } else if (hasCssPropsNamespace) {
          cssPropsChanges[key] = value;
        } else {
          // default to cssCustomProperties (covers custom-only and the "no metadata" edge)
          customPropsChanges[key] = value;
        }
      }
    }

    return { cssPropsChanges, customPropsChanges };
  }

  /**
   * Internal method to set properties directly without propagation.
   *
   * @private
   * @param changes - Properties to change (key-value pairs)
   * @param currentValues - Current aggregated values
   * @returns Promise<AggregatedStyleValues> - The new state after successful update
   */
  private async setPropertiesDirectlyInternal(
    changes: Partial<Record<string, string>>,
    currentValues: AggregatedStyleValues,
  ): Promise<AggregatedStyleValues> {
    // Split changes by namespace based on metadata
    const cssPropsChanges: Record<string, string> = {};
    const customPropsChanges: Record<string, string> = {};

    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) {
        continue;
      }

      const meta = currentValues.metadata[key];
      if (!meta) {
        continue;
      }

      if (meta.namespace === 'cssProperties') {
        cssPropsChanges[key] = value;
      } else {
        customPropsChanges[key] = value;
      }
    }

    // Get updates from both engines in parallel
    const cssPropsPromise: Promise<Record<string, string>> =
      Object.keys(cssPropsChanges).length > 0
        ? this.cssPropertiesEngine.setPropertiesDirectly(cssPropsChanges, {
            metadata: this.filterMetadataByNamespace(
              currentValues.metadata,
              'cssProperties',
            ),
          })
        : Promise.resolve({});

    const customPropsPromise: Promise<Record<string, string>> =
      Object.keys(customPropsChanges).length > 0
        ? this.customPropertiesEngine.setPropertiesDirectly(
            customPropsChanges,
            this.filterMetadataByNamespace(
              currentValues.metadata,
              'cssCustomProperties',
            ),
          )
        : Promise.resolve({});

    const [cssPropsUpdates, customPropsUpdates] = await Promise.all([
      cssPropsPromise,
      customPropsPromise,
    ]);

    // Apply consolidated updates in single SDK call
    const hasUpdates =
      Object.keys(cssPropsUpdates).length > 0 ||
      Object.keys(customPropsUpdates).length > 0;

    if (hasUpdates) {
      await this.sdkClient.setStyles({
        ...(Object.keys(cssPropsUpdates).length > 0 && {
          cssProperties: cssPropsUpdates,
        }),
        ...(Object.keys(customPropsUpdates).length > 0 && {
          cssCustomProperties: customPropsUpdates,
        }),
      });
    }

    // Merge updates with current state to create new aggregated values
    // Pass original changes to track which were shorthands
    return this.mergeUpdatesWithCurrentValues(
      currentValues,
      cssPropsUpdates,
      customPropsUpdates,
    );
  }

  /**
   * Updates properties with smart propagation.
   * NOW STATEFUL: Uses internal state, queues update for sequential processing.
   *
   * @param changes - Property changes as key-value pairs
   * @returns Promise<AggregatedStyleValues> - The new state after update
   *
   * @example
   * const newValues = await engine.updateProperties({ borderWidth: '2px' });
   */
  async updateProperties(
    changes: Partial<Record<string, string>>,
  ): Promise<AggregatedStyleValues> {
    return this.enqueueUpdate({
      changes,
      isDirect: false,
      resolve: () => {},
      reject: () => {},
    });
  }

  /**
   * Sets properties directly without smart propagation.
   * NOW STATEFUL: Uses internal state, queues update for sequential processing.
   *
   * @param changes - Property changes as key-value pairs
   * @returns Promise<AggregatedStyleValues> - The new state after update
   *
   * @example
   * const newValues = await engine.setPropertiesDirectly({ borderLeftWidth: '5px' });
   */
  async setPropertiesDirectly(
    changes: Partial<Record<string, string>>,
  ): Promise<AggregatedStyleValues> {
    return this.enqueueUpdate({
      changes,
      isDirect: true,
      resolve: () => {},
      reject: () => {},
    });
  }

  // Helper methods

  /**
   * Merges updates with current aggregated values to create new state.
   * Efficiently updates by merging raw values then re-decomposing the complete result.
   *
   * Infrastructure Rule: When adding a new key to raw (from what we send to SDK):
   * 1. Override the same existing key if it exists
   * 2. If the key is a shorthand, remove all its atomics (any nesting level) from current raw
   *
   * @param currentValues - Current aggregated values
   * @param cssPropsUpdates - CSS property updates to merge (what we send to SDK)
   * @param customPropsUpdates - Custom property updates to merge
   * @returns New aggregated values with updates applied
   */
  private mergeUpdatesWithCurrentValues(
    currentValues: AggregatedStyleValues,
    cssPropsUpdates: Record<string, string>,
    customPropsUpdates: Record<string, string>,
  ): AggregatedStyleValues {
    // Extract current raw values based on mode
    const currentCSSRaw =
      currentValues.mode === 'cssCustomProperties'
        ? {}
        : currentValues.raw.cssProperties;

    const currentCustomRaw =
      currentValues.mode === 'cssProperties'
        ? {}
        : currentValues.raw.cssCustomProperties;

    // Handle CSS properties with shorthand-atomic rule
    const newCSSRaw = this.mergeCSSPropertiesWithShorthandRule(
      currentCSSRaw,
      cssPropsUpdates,
    );
    // Custom properties: simple merge (no complex shorthand-atomic rules)
    const newCustomRaw = { ...currentCustomRaw, ...customPropsUpdates };

    const customPropsDefinitions =
      currentValues.mode === 'cssProperties'
        ? {}
        : currentValues.definitions.cssCustomProperties ?? {};

    const isGdWdsEnabled =
      this.componentsAPI.experimentsAPI?.enabled(GD_WDS_EXPERIMENT) ?? false;

    if (isGdWdsEnabled || this.withVariablesResolution) {
      const cssPropsDefinitions =
        currentValues.mode === 'cssCustomProperties'
          ? {}
          : currentValues.definitions.cssProperties ?? {};

      return super.deriveAggregatedValues(
        newCSSRaw,
        newCustomRaw,
        cssPropsDefinitions,
        customPropsDefinitions,
        currentValues.metadata,
        this.componentsAPI,
      );
    }

    // Without variable resolution: manual decomposition
    const newCSSDecomposed = decomposeCSSProperties(newCSSRaw);
    const newCustomDecomposed = decomposePropertiesWithDefinitionsAndNesting(
      Object.values(customPropsDefinitions)
        .map((definition) => definition.cssPropertyType)
        .filter(
          (type): type is CssPropertyEnumCssPropertyType => type !== undefined,
        ),
      newCustomRaw,
      (key) => currentValues.metadata?.[key]?.cssPropertyType,
    );

    if (currentValues.mode === 'mixed') {
      return {
        mode: 'mixed',
        raw: {
          cssProperties: newCSSRaw,
          cssCustomProperties: newCustomRaw,
        },
        decomposed: {
          cssProperties: newCSSDecomposed,
          cssCustomProperties: newCustomDecomposed,
        },
        metadata: currentValues.metadata,
        definitions: currentValues.definitions,
      };
    } else if (currentValues.mode === 'cssProperties') {
      return {
        mode: 'cssProperties',
        raw: {
          cssProperties: newCSSRaw,
        },
        decomposed: {
          cssProperties: newCSSDecomposed,
        },
        metadata: currentValues.metadata,
        definitions: currentValues.definitions,
      };
    } else {
      return {
        mode: 'cssCustomProperties',
        raw: {
          cssCustomProperties: newCustomRaw,
        },
        decomposed: {
          cssCustomProperties: newCustomDecomposed,
        },
        metadata: currentValues.metadata,
        definitions: currentValues.definitions,
      };
    }
  }

  /**
   * Merges CSS properties with infrastructure rule:
   * When adding a new key to raw (from what we send to SDK):
   * 1. Override the same existing key if it exists
   * 2. If the key is a shorthand, remove all its atomics (any nesting level) from current raw
   *
   * @param currentRaw - Current raw CSS properties
   * @param updates - CSS property updates to merge (what we're sending to SDK)
   * @returns Merged raw CSS properties
   *
   * @example
   * Current: { borderLeftWidth: '1px', borderTopStyle: 'dashed', color: 'red' }
   * Updates: { border: '2px solid blue' }
   * Result: { border: '2px solid blue', color: 'red' } // All border atomics removed, color preserved
   */
  private mergeCSSPropertiesWithShorthandRule(
    currentRaw: Record<string, string>,
    updates: Record<string, string>,
  ): Record<string, string> {
    // Collect all atomics that need to be removed
    const atomicsToRemove = new Set<string>();

    // For each update key (what we're sending to SDK), if it's a shorthand, collect all its atomics to remove
    for (const updateKey of Object.keys(updates)) {
      if (isShorthand(updateKey)) {
        const atomics = this.getAllAtomicsRecursively(updateKey);
        atomics.forEach((atomic) => atomicsToRemove.add(atomic));
      }
    }

    // Add updates first so their decomposed children appear first at each nesting level,
    // giving them priority over same-level children from co-existing shorthands.
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(updates)) {
      result[key] = value;
    }

    // Add remaining current values, skipping atomics removed by shorthand rule
    // and keys already set by updates.
    for (const [key, value] of Object.entries(currentRaw)) {
      if (!atomicsToRemove.has(key) && !(key in result)) {
        result[key] = value;
      }
    }

    return result;
  }

  /**
   * Gets all atomics of a shorthand property recursively.
   * For example, 'border' returns all border atomics including intermediate
   * shorthands like 'borderWidth', 'borderColor', etc.
   *
   * @param shorthand - Shorthand property name
   * @returns Set of all atomic property names
   */
  private getAllAtomicsRecursively(shorthand: string): Set<string> {
    const atomics = new Set<string>();
    const toProcess = [shorthand];

    while (toProcess.length > 0) {
      const current = toProcess.shift()!;

      if (!isShorthand(current)) {
        // It's atomic
        atomics.add(current);
        continue;
      }

      // Get immediate children of this shorthand
      const children = getShorthandAtomics(current);
      if (children) {
        for (const child of children) {
          if (isShorthand(child)) {
            // It's an intermediate shorthand, process it recursively
            toProcess.push(child);
          } else {
            // It's atomic
            atomics.add(child);
          }
        }
      }
    }

    return atomics;
  }

  private filterMetadataByNamespace(
    metadata: Record<string, PropertyMetadata>,
    namespace: 'cssProperties' | 'cssCustomProperties',
  ): Record<string, PropertyMetadata> {
    const filtered: Record<string, PropertyMetadata> = {};

    for (const [key, meta] of Object.entries(metadata)) {
      if (meta.namespace === namespace) {
        filtered[key] = meta;
      }
    }

    return filtered;
  }

  /**
   * Enqueues an update and triggers queue processing.
   *
   * @private
   */
  private enqueueUpdate(update: QueuedUpdate): Promise<AggregatedStyleValues> {
    return new Promise<AggregatedStyleValues>((resolve, reject) => {
      update.resolve = resolve;
      update.reject = reject;

      this.updateQueue.push(update);

      // Trigger processing (non-blocking)
      this.processQueue();
    });
  }

  /**
   * Processes the update queue sequentially.
   * Ensures only one update runs at a time.
   *
   * @private
   */
  private async processQueue(): Promise<void> {
    // If already processing, return existing promise
    if (this.processingPromise) {
      return this.processingPromise;
    }

    // Start processing
    this.processingPromise = (async () => {
      while (this.updateQueue.length > 0) {
        // Check cancellation
        if (this.isCancelled) {
          return;
        }

        const update = this.updateQueue.shift()!;

        if (!this.currentMetadata) {
          update.reject(
            new Error('Engine not initialized - call initialize() first'),
          );
          continue;
        }

        // Lazy-fetch values when metadata is ready but values haven't been
        // fetched yet (e.g. engine created via fromMetadataEngine()).
        if (!this.currentValues) {
          this.currentValues = await this.getAllValuesInternal(
            this.currentMetadata,
          );
        }

        try {
          // Apply update using internal methods
          let updatedValues: AggregatedStyleValues;
          if (update.isDirect) {
            updatedValues = await this.setPropertiesDirectlyInternal(
              update.changes,
              this.currentValues,
            );
          } else {
            updatedValues = await this.updatePropertiesInternal(
              update.changes,
              this.currentValues,
            );
          }

          // Optional refetch for consistency
          if (this.shouldRefetchAfterUpdate) {
            updatedValues = await this.getAllValuesInternal(
              this.currentMetadata,
            );
          }

          // Check cancellation before updating state
          if (this.isCancelled) {
            update.reject(new Error('Engine cancelled'));
            return;
          }

          // Update internal state
          this.currentValues = updatedValues;

          // Resolve promise with updated values
          update.resolve(updatedValues);
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Update failed');

          // Reject promise
          update.reject(error);

          // Refetch on error to maintain consistency
          if (!this.isCancelled && this.currentMetadata) {
            try {
              const recoveredValues = await this.getAllValuesInternal(
                this.currentMetadata,
              );
              this.currentValues = recoveredValues;
            } catch (refetchErr) {
              console.error('[Engine] Refetch after error failed:', refetchErr);
            }
          }
        }
      }
    })();

    // Cleanup
    try {
      await this.processingPromise;
    } finally {
      this.processingPromise = null;
    }
  }
}
